import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/use-auth";
import { AppLayout } from "@/components/layout";
import { apiFetch } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import { format } from "date-fns";
import {
  Send, Mail, MessageSquare, Users, CheckCircle, AlertCircle,
  ArrowLeft, Eye, Loader2, History, Phone
} from "lucide-react";

type Recipient = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  role: string;
  createdAt: string;
};

type SentMessage = {
  id: string;
  audience: "ALL_TRAINERS" | "ALL_STUDENTS" | "SELECTED";
  channel: "EMAIL" | "SMS" | "BOTH";
  subject: string | null;
  body: string;
  recipientCount: number;
  emailsSent: number;
  smsSent: number;
  emailsFailed: number;
  smsFailed: number;
  createdAt: string;
};

type Step = "compose" | "preview" | "sent";

export default function AdminMessaging() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const gymId = user?.gymId ?? "";

  const [trainers, setTrainers] = useState<Recipient[]>([]);
  const [students, setStudents] = useState<Recipient[]>([]);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState<SentMessage[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  const [step, setStep] = useState<Step>("compose");
  const [audienceTab, setAudienceTab] = useState<string>("trainers");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [audienceMode, setAudienceMode] = useState<"all" | "selected">("all");
  const [channel, setChannel] = useState<"EMAIL" | "SMS" | "BOTH">("EMAIL");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<{
    recipientCount: number;
    emailsSent: number;
    emailsFailed: number;
    smsSent: number;
    smsFailed: number;
  } | null>(null);

  useEffect(() => {
    if (!gymId) return;
    apiFetch(`/admin/users/gym/${gymId}`)
      .then((data: Recipient[]) => {
        setTrainers(data.filter(u => u.role === "TRAINER"));
        setStudents(data.filter(u => u.role === "STUDENT"));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [gymId]);

  useEffect(() => {
    if (!gymId) return;
    apiFetch<SentMessage[]>("/admin/messages")
      .then(setHistory)
      .catch(() => {})
      .finally(() => setHistoryLoading(false));
  }, [gymId]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const preselected = params.get("recipients");
    const role = params.get("role");
    if (preselected) {
      const ids = preselected.split(",");
      setSelectedIds(new Set(ids));
      setAudienceMode("selected");
      if (role === "TRAINER") setAudienceTab("trainers");
      else if (role === "STUDENT") setAudienceTab("students");
    }
  }, []);

  const currentList = audienceTab === "trainers" ? trainers : students;

  const toggleRecipient = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (currentList.every(r => selectedIds.has(r.id))) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        currentList.forEach(r => next.delete(r.id));
        return next;
      });
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        currentList.forEach(r => next.add(r.id));
        return next;
      });
    }
  };

  const getAudienceLabel = () => {
    if (audienceMode === "all") {
      return audienceTab === "trainers" ? "All Trainers" : "All Students";
    }
    return `${selectedIds.size} selected recipient${selectedIds.size !== 1 ? "s" : ""}`;
  };

  const getAudienceValue = (): "ALL_TRAINERS" | "ALL_STUDENTS" | "SELECTED" => {
    if (audienceMode === "all") {
      return audienceTab === "trainers" ? "ALL_TRAINERS" : "ALL_STUDENTS";
    }
    return "SELECTED";
  };

  const getAudienceCount = () => {
    if (audienceMode === "selected") return selectedIds.size;
    return audienceTab === "trainers" ? trainers.length : students.length;
  };

  const canSend = () => {
    if (!body.trim()) return false;
    if ((channel === "EMAIL" || channel === "BOTH") && !subject.trim()) return false;
    if (audienceMode === "selected" && selectedIds.size === 0) return false;
    if (audienceMode === "all" && getAudienceCount() === 0) return false;
    return true;
  };

  const handleSend = async () => {
    if (!canSend()) return;
    setSending(true);
    try {
      const result = await apiFetch<{
        recipientCount: number;
        emailsSent: number;
        emailsFailed: number;
        smsSent: number;
        smsFailed: number;
      }>("/admin/messages", {
        method: "POST",
        body: JSON.stringify({
          audience: getAudienceValue(),
          channel,
          subject: subject.trim() || undefined,
          body: body.trim(),
          recipientIds: audienceMode === "selected" ? Array.from(selectedIds) : undefined,
        }),
      });
      setSendResult(result);
      setStep("sent");
      toast({ title: "Message sent!", description: `Delivered to ${result.recipientCount} recipient${result.recipientCount !== 1 ? "s" : ""}.` });
      apiFetch<SentMessage[]>("/admin/messages").then(setHistory).catch(() => {});
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Failed to send message";
      toast({ title: msg, variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  const resetForm = () => {
    setStep("compose");
    setSubject("");
    setBody("");
    setSelectedIds(new Set());
    setAudienceMode("all");
    setSendResult(null);
  };

  const getAudienceBadge = (msg: SentMessage) => {
    switch (msg.audience) {
      case "ALL_TRAINERS": return <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">All Trainers</Badge>;
      case "ALL_STUDENTS": return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">All Students</Badge>;
      case "SELECTED": return <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30">{msg.recipientCount} Selected</Badge>;
    }
  };

  const getChannelBadge = (ch: string) => {
    switch (ch) {
      case "EMAIL": return <Badge variant="outline" className="text-xs"><Mail className="w-3 h-3 mr-1" />Email</Badge>;
      case "SMS": return <Badge variant="outline" className="text-xs"><Phone className="w-3 h-3 mr-1" />SMS</Badge>;
      case "BOTH": return <Badge variant="outline" className="text-xs"><MessageSquare className="w-3 h-3 mr-1" />Both</Badge>;
      default: return null;
    }
  };

  return (
    <AppLayout>
      <div className="flex flex-col gap-8 pb-12">
        <div>
          <h1 className="text-4xl font-display font-bold text-white mb-2">Messaging Center</h1>
          <p className="text-muted-foreground text-lg">Send messages to your trainers and students via email or SMS.</p>
        </div>

        {step === "compose" && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <Card className="glass-panel border-white/5">
                <CardHeader>
                  <CardTitle className="text-lg font-display uppercase tracking-wide">Audience</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex gap-3">
                    <Button
                      variant={audienceMode === "all" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setAudienceMode("all")}
                    >
                      {audienceTab === "trainers" ? "All Trainers" : "All Students"}
                    </Button>
                    <Button
                      variant={audienceMode === "selected" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setAudienceMode("selected")}
                    >
                      Select Individually
                    </Button>
                  </div>

                  <Tabs value={audienceTab} onValueChange={(v) => { setAudienceTab(v); }}>
                    <TabsList className="bg-white/5">
                      <TabsTrigger value="trainers">Trainers ({trainers.length})</TabsTrigger>
                      <TabsTrigger value="students">Students ({students.length})</TabsTrigger>
                    </TabsList>

                    {audienceMode === "selected" && (
                      <>
                        <TabsContent value="trainers" className="mt-4">
                          <RecipientList
                            recipients={trainers}
                            selectedIds={selectedIds}
                            onToggle={toggleRecipient}
                            onToggleAll={toggleSelectAll}
                            loading={loading}
                          />
                        </TabsContent>
                        <TabsContent value="students" className="mt-4">
                          <RecipientList
                            recipients={students}
                            selectedIds={selectedIds}
                            onToggle={toggleRecipient}
                            onToggleAll={toggleSelectAll}
                            loading={loading}
                          />
                        </TabsContent>
                      </>
                    )}
                  </Tabs>
                </CardContent>
              </Card>

              <Card className="glass-panel border-white/5">
                <CardHeader>
                  <CardTitle className="text-lg font-display uppercase tracking-wide">Message</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Delivery Channel</Label>
                    <Select value={channel} onValueChange={(v) => setChannel(v as "EMAIL" | "SMS" | "BOTH")}>
                      <SelectTrigger className="glass-panel border-white/10">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="EMAIL">Email Only</SelectItem>
                        <SelectItem value="SMS">SMS Only</SelectItem>
                        <SelectItem value="BOTH">Email & SMS</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {(channel === "EMAIL" || channel === "BOTH") && (
                    <div className="space-y-2">
                      <Label>Subject Line</Label>
                      <Input
                        className="glass-panel border-white/10"
                        value={subject}
                        onChange={e => setSubject(e.target.value)}
                        placeholder="e.g. Schedule Update for This Week"
                      />
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>Message Body</Label>
                    <Textarea
                      className="glass-panel border-white/10 min-h-[150px]"
                      value={body}
                      onChange={e => setBody(e.target.value)}
                      placeholder="Type your message here..."
                    />
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="space-y-6">
              <Card className="glass-panel border-white/5">
                <CardHeader>
                  <CardTitle className="text-lg font-display uppercase tracking-wide">Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Audience</span>
                    <span className="text-white font-medium">{getAudienceLabel()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Channel</span>
                    <span className="text-white font-medium">
                      {channel === "EMAIL" ? "Email" : channel === "SMS" ? "SMS" : "Email & SMS"}
                    </span>
                  </div>
                  {subject && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Subject</span>
                      <span className="text-white font-medium truncate max-w-[180px]">{subject}</span>
                    </div>
                  )}
                  <div className="pt-3 border-t border-white/5">
                    <Button
                      className="w-full gap-2"
                      disabled={!canSend()}
                      onClick={() => setStep("preview")}
                    >
                      <Eye className="w-4 h-4" /> Preview Message
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {step === "preview" && (
          <div className="max-w-2xl mx-auto space-y-6">
            <Card className="glass-panel border-white/5">
              <CardHeader>
                <CardTitle className="text-lg font-display uppercase tracking-wide">Preview</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  {getChannelBadge(channel)}
                  <Badge variant="outline" className="text-xs">
                    <Users className="w-3 h-3 mr-1" />
                    {getAudienceLabel()}
                  </Badge>
                </div>

                {(channel === "EMAIL" || channel === "BOTH") && (
                  <div className="p-4 rounded-xl bg-white/[0.03] border border-white/5">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Email Subject</p>
                    <p className="text-white font-semibold">{subject}</p>
                  </div>
                )}

                <div className="p-4 rounded-xl bg-white/[0.03] border border-white/5">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Message Body</p>
                  <p className="text-white whitespace-pre-wrap leading-relaxed">{body}</p>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button variant="outline" onClick={() => setStep("compose")} className="gap-2">
                    <ArrowLeft className="w-4 h-4" /> Edit
                  </Button>
                  <Button onClick={handleSend} disabled={sending || !canSend()} className="flex-1 gap-2">
                    {sending ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</>
                    ) : (
                      <><Send className="w-4 h-4" /> Send Now</>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {step === "sent" && sendResult && (
          <div className="max-w-2xl mx-auto space-y-6">
            <Card className="glass-panel border-white/5">
              <CardContent className="flex flex-col items-center justify-center py-12 text-center gap-4">
                <CheckCircle className="w-16 h-16 text-green-400" />
                <h2 className="text-2xl font-display font-bold text-white">Message Sent!</h2>
                <p className="text-muted-foreground">
                  Your message was sent to {sendResult.recipientCount} recipient{sendResult.recipientCount !== 1 ? "s" : ""}.
                </p>

                <div className="grid grid-cols-2 gap-4 w-full max-w-sm mt-4">
                  {(channel === "EMAIL" || channel === "BOTH") && (
                    <>
                      <div className="p-3 rounded-xl bg-green-500/10 border border-green-500/20 text-center">
                        <p className="text-2xl font-bold text-green-400">{sendResult.emailsSent}</p>
                        <p className="text-xs text-muted-foreground">Emails Sent</p>
                      </div>
                      {sendResult.emailsFailed > 0 && (
                        <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-center">
                          <p className="text-2xl font-bold text-red-400">{sendResult.emailsFailed}</p>
                          <p className="text-xs text-muted-foreground">Emails Failed</p>
                        </div>
                      )}
                    </>
                  )}
                  {(channel === "SMS" || channel === "BOTH") && (
                    <>
                      <div className="p-3 rounded-xl bg-green-500/10 border border-green-500/20 text-center">
                        <p className="text-2xl font-bold text-green-400">{sendResult.smsSent}</p>
                        <p className="text-xs text-muted-foreground">SMS Sent</p>
                      </div>
                      {sendResult.smsFailed > 0 && (
                        <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-center">
                          <p className="text-2xl font-bold text-red-400">{sendResult.smsFailed}</p>
                          <p className="text-xs text-muted-foreground">SMS Failed</p>
                        </div>
                      )}
                    </>
                  )}
                </div>

                <Button onClick={resetForm} className="mt-4 gap-2">
                  <Send className="w-4 h-4" /> Send Another Message
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        <Card className="glass-panel border-white/5">
          <CardHeader>
            <CardTitle className="text-lg font-display uppercase tracking-wide flex items-center gap-2">
              <History className="w-5 h-5" /> Sent Messages
            </CardTitle>
          </CardHeader>
          <CardContent>
            {historyLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => <div key={i} className="h-16 bg-white/5 rounded-xl animate-pulse" />)}
              </div>
            ) : history.length === 0 ? (
              <div className="p-8 text-center rounded-xl border border-dashed border-white/10 bg-white/[0.02]">
                <MessageSquare className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-50" />
                <p className="text-muted-foreground">No messages sent yet.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {history.map(msg => (
                  <div key={msg.id} className="flex items-center justify-between p-4 rounded-xl bg-white/[0.03] border border-white/5 hover:border-white/10 transition-colors">
                    <div className="flex items-center gap-4 min-w-0">
                      <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                        {msg.channel === "SMS" ? <Phone className="w-4 h-4 text-primary" /> : <Mail className="w-4 h-4 text-primary" />}
                      </div>
                      <div className="min-w-0">
                        <p className="text-white font-semibold text-sm truncate">
                          {msg.subject || "(No subject)"}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">{msg.body.slice(0, 80)}{msg.body.length > 80 ? "..." : ""}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0 ml-4">
                      {getAudienceBadge(msg)}
                      {getChannelBadge(msg.channel)}
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">
                          {format(new Date(msg.createdAt), "MMM d, yyyy")}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {format(new Date(msg.createdAt), "h:mm a")}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        {(msg.emailsSent > 0 || msg.smsSent > 0) && (
                          <CheckCircle className="w-4 h-4 text-green-400" />
                        )}
                        {(msg.emailsFailed > 0 || msg.smsFailed > 0) && (
                          <AlertCircle className="w-4 h-4 text-amber-400" />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}

function RecipientList({
  recipients,
  selectedIds,
  onToggle,
  onToggleAll,
  loading,
}: {
  recipients: Recipient[];
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
  onToggleAll: () => void;
  loading: boolean;
}) {
  if (loading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map(i => <div key={i} className="h-12 bg-white/5 rounded-lg animate-pulse" />)}
      </div>
    );
  }

  if (recipients.length === 0) {
    return (
      <div className="p-6 text-center rounded-xl border border-dashed border-white/10 bg-white/[0.02]">
        <p className="text-muted-foreground text-sm">No members in this category.</p>
      </div>
    );
  }

  const allSelected = recipients.every(r => selectedIds.has(r.id));

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3 p-2 rounded-lg bg-white/[0.03] border border-white/5">
        <Checkbox
          checked={allSelected}
          onCheckedChange={onToggleAll}
        />
        <span className="text-sm text-muted-foreground font-medium">
          Select All ({recipients.length})
        </span>
      </div>
      <div className="max-h-[300px] overflow-y-auto space-y-1">
        {recipients.map(r => (
          <div
            key={r.id}
            className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-white/[0.03] transition-colors cursor-pointer"
            onClick={() => onToggle(r.id)}
          >
            <Checkbox
              checked={selectedIds.has(r.id)}
              onCheckedChange={() => onToggle(r.id)}
            />
            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold text-primary shrink-0">
              {r.firstName[0]}{r.lastName[0]}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm text-white font-medium truncate">{r.firstName} {r.lastName}</p>
              <p className="text-xs text-muted-foreground truncate">{r.email}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {r.email && <Mail className="w-3.5 h-3.5 text-muted-foreground" />}
              {r.phone && <Phone className="w-3.5 h-3.5 text-muted-foreground" />}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
